import { diffChars } from 'diff';
import lexer, { type JSXToken, type Token } from 'js-tokens';
import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import MagicString from '~/lib/static-analysis/magic';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

interface Insert {
  index: number;
  value: string;
}

interface Scope {
  value: '{' | '[' | '(';
  level: number;
  indent: string;
}

const openers = new Set(['{', '[', '(']);
const closers = new Set(['}', ']', ')']);

const charMap: Record<string, string> = {
  '{': '}',
  '[': ']',
  '(': ')',
};

export async function transformClosingBrackets(code: MagicString, path: string) {
  const result = getMissingClosing(code.str, path);

  if (result.isValid) {
    return;
  }

  // we fixed it (or we think we did)
  if (result.isFixed) {
    result.applyChanges(code);
    track({ type: 'static-fixed', code: code.str });

    return;
  }

  // we couldn't fix it, let's see what the LLM can do
  try {
    const fixedOutput = await llmFallback(code.str);

    const result = getMissingClosing(fixedOutput, path);

    if (result.isValid) {
      // the LLM fixed it
      const changes = calculateChanges(code.str, fixedOutput);

      changes.forEach((change) => {
        code.replace(...change);
      });

      track({ type: 'llm-fixed', code: code.str });

      return;
    }

    if (result.isFixed) {
      // the LLM + code transform fixed it
      result.applyChanges(code);
      track({ type: 'llm+static-fixed', code: code.str });

      return;
    }

    // neither us nor the LLM could fix it
    track({ type: 'did-not-fix', code: code.str });
  } catch (error: any) {
    track({ type: 'llm-error', code: code.str, errorMessage: error.message });
    console.error(error);
  }
}

export function getMissingClosing(code: string, path: string) {
  const tokens: (Token | JSXToken)[] = Array.from(lexer(code, { jsx: path.endsWith('.tsx') || path.endsWith('.jsx') }));

  if (verifyBrackets(tokens)) {
    return { isValid: true };
  }

  const isSvelte = path.endsWith('.svelte');

  const changes: Insert[] = [];

  let cursor = 0;
  let scopeLevel = 0;
  let indent = '';
  let startOfNewLine = true;

  const scopeStack: Scope[] = [];

  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
    let token = tokens[tokenIndex];

    const index = cursor;
    const isLastToken = tokenIndex === tokens.length - 1;

    cursor += token.value.length;

    if (!isLastToken && token.type === 'LineTerminatorSequence') {
      startOfNewLine = true;
      continue;
    }

    if (startOfNewLine) {
      startOfNewLine = false;

      indent = token.type === 'WhiteSpace' ? token.value : '';

      if (scopeStack.length > 0) {
        let scope = scopeStack.at(-1)!;

        // pop scopes until you get to the associated indent level
        while (scopeStack.length && scopeLevel > scope.level && indent.length <= scope.indent.length) {
          let lineHasClosingBracket = false;

          for (let i = tokenIndex; i < tokens.length; i++) {
            const lineToken = tokens[i];
            const isPunctuator = lineToken.type === 'Punctuator' || lineToken.type === 'JSXPunctuator';

            if (isPunctuator && lineToken.value === charMap[scope.value]) {
              lineHasClosingBracket = true;
              break;
            } else if (
              (isPunctuator && lineToken.value === scope.value) ||
              lineToken.type === 'LineTerminatorSequence'
            ) {
              break;
            }
          }

          if (lineHasClosingBracket) {
            break;
          }

          /**
           * Backtrack through empty lines to improve formatting.
           *
           * Uses -1 to ignore the last newline because the line it's associated with is non-empty.
           */
          let prevEmptyLines = -1;

          for (let i = tokenIndex - 1; i > 0 && tokens[i].type === 'LineTerminatorSequence'; i--) {
            prevEmptyLines++;
          }

          changes.push({
            index: prevEmptyLines > 0 ? index - prevEmptyLines : index,
            value: `${scope.indent}${charMap[scope.value]}\n`,
          });

          scopeLevel--;
          scopeStack.pop();

          if (scopeStack.length > 0) {
            scope = scopeStack.at(-1)!;
          }
        }
      }
    }

    // patch tokens for svelte template conditional closing tags
    if (
      isSvelte &&
      token.type === 'RegularExpressionLiteral' &&
      token.closed === false &&
      token.value.at(-1) === '}' &&
      scopeStack.at(-1)?.value === '{'
    ) {
      token = tokens[tokenIndex] = {
        type: 'Punctuator',
        value: '}',
      };
    }

    if (token.type === 'Punctuator' || token.type === 'JSXPunctuator') {
      if (openers.has(token.value)) {
        scopeStack.push({
          value: token.value as Scope['value'],
          level: scopeLevel,
          indent,
        });

        scopeLevel++;
        continue;
      } else if (closers.has(token.value)) {
        scopeLevel--;

        if (scopeStack.length > 0) {
          const scope = scopeStack.at(-1)!;

          if (
            token.value === charMap[scope.value] &&
            indent.length <= scope.indent.length &&
            scopeLevel >= scope.level
          ) {
            // if indents are not equal, inject whitespace
            if (indent.length !== scope.indent.length) {
              changes.push({
                index,
                value: scope.indent,
              });
            }

            scopeStack.pop();
          }
        }

        continue;
      }
    }
  }

  while (scopeStack.length > 0) {
    const scope = scopeStack.at(-1)!;

    if (scopeLevel >= scope.level) {
      changes.push({
        index: cursor,
        value: `\n${scope.indent}${charMap[scope.value]}`,
      });
      scopeLevel--;
    }

    scopeStack.pop();
  }

  const isFixed = scopeLevel === 0;

  return {
    changes,
    isFixed,
    applyChanges: (code: MagicString) => {
      changes.forEach((change) => {
        code.insert(change.index, change.value);
      });
    },
  };
}

function verifyBrackets(tokens: (Token | JSXToken)[]): boolean {
  const stack: string[] = [];

  for (const { type, value } of tokens) {
    if (type !== 'Punctuator' && type !== 'JSXPunctuator') {
      continue;
    }

    if (openers.has(value)) {
      stack.push(value);
    } else if (closers.has(value)) {
      const lastOpener = stack.pop();

      if (!lastOpener || charMap[lastOpener] !== value) {
        return false;
      }
    }
  }

  return stack.length === 0;
}

async function llmFallback(code: string) {
  const response = await clientFetch('/api/bracket/fix', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileContent: code,
    }),
  });

  return await response.text();
}

type DiffChange = Parameters<InstanceType<typeof MagicString>['replace']>;

function calculateChanges(original: string, _updated: string) {
  // normalise whitespace at the start of file (model likes to ignore it)
  const start = (original.match(/^\s+/) ?? [''])[0];
  const end = (original.match(/\s+$/) ?? [''])[0];
  const updated = start + _updated.trim() + end;

  // calculate diff
  const diff = diffChars(original, updated);
  const changes: DiffChange[] = [];

  let index = 0;

  diff.forEach((part) => {
    if (part.added) {
      changes.push([index, index, part.value]);
      return;
    }

    if (part.removed) {
      changes.push([index, index + part.value.length, '']);
    }

    index += part.value.length;
  });

  return changes;
}

interface TrackOptions {
  type: 'static-fixed' | 'llm-fixed' | 'llm+static-fixed' | 'did-not-fix' | 'llm-error';
  code?: string;
  errorMessage?: string;
}

function track(properties: TrackOptions) {
  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.BracketFixing,
      properties,
    },
  });
}
